package com.evoapps.evoshift.domain.spreadsheet

/**
 * The entries of a zip file (an .xlsx is one), from its central directory: stored and deflated
 * entries only, which is all spreadsheet programs write. Names are UTF-8.
 *
 * Guards ("zip bombs" and absurd files): at most [maxEntries] entries; an entry's declared size
 * over [maxEntrySize], or all entries' declared sizes together over [maxTotalSize], fail before
 * anything is inflated; and inflating stops at the declared size, so an entry that lies about its
 * size cannot expand any further. Encrypted entries fail as [SpreadsheetProblem.PASSWORD_PROTECTED].
 */
class ZipReader(
    private val bytes: ByteArray,
    private val maxEntries: Int = MAX_ENTRIES,
    private val maxEntrySize: Int = MAX_ENTRY_SIZE,
    private val maxTotalSize: Long = MAX_TOTAL_SIZE.toLong()
) {

    private class Entry(val method: Int, val flags: Int, val compressedSize: Int, val size: Int, val localHeader: Int)

    private val entries: Map<String, Entry> = readDirectory()

    val names: Set<String> get() = entries.keys

    /** The entry's content, or null when the archive has no such entry. */
    suspend fun read(name: String): ByteArray? {
        val entry = entries[name] ?: return null
        if (entry.flags and FLAG_ENCRYPTED != 0) throw SpreadsheetException(SpreadsheetProblem.PASSWORD_PROTECTED)
        val header = entry.localHeader
        if (u32(header) != LOCAL_SIGNATURE) throw corrupt("bad local header: $name")
        val dataStart = header + 30 + u16(header + 26) + u16(header + 28)
        if (dataStart < 0 || dataStart + entry.compressedSize > bytes.size) throw corrupt("truncated entry: $name")
        val data = when (entry.method) {
            METHOD_STORED -> bytes.copyOfRange(dataStart, dataStart + entry.compressedSize)
            METHOD_DEFLATED -> try {
                inflateRaw(bytes.copyOfRange(dataStart, dataStart + entry.compressedSize), maxOutput = entry.size)
            } catch (e: SpreadsheetException) {
                throw e
            } catch (e: Exception) {
                throw corrupt("cannot inflate $name: ${e.message}")
            }
            else -> throw corrupt("compression method ${entry.method}: $name")
        }
        if (data.size != entry.size) throw corrupt("size mismatch: $name")
        return data
    }

    private fun readDirectory(): Map<String, Entry> {
        // The end-of-central-directory record: the last 22 bytes plus a comment of up to 64 KiB.
        var eocd = bytes.size - 22
        val lowest = maxOf(0, bytes.size - 22 - 0xFFFF)
        while (eocd >= lowest && u32(eocd) != END_SIGNATURE) eocd--
        if (eocd < lowest) throw corrupt("not a zip file")
        val count = u16(eocd + 10)
        if (count > maxEntries) throw SpreadsheetException(SpreadsheetProblem.TOO_LARGE_UNPACKED, "$count entries")
        var at = u32(eocd + 16)
        if (at > Int.MAX_VALUE) throw corrupt("bad central directory")
        val result = LinkedHashMap<String, Entry>()
        var total = 0L
        repeat(count) {
            val offset = at.toInt()
            if (offset + 46 > bytes.size || u32(offset) != CENTRAL_SIGNATURE) throw corrupt("bad central directory")
            val flags = u16(offset + 8)
            val method = u16(offset + 10)
            val compressedSize = u32(offset + 20)
            val size = u32(offset + 24)
            val nameLength = u16(offset + 28)
            val extraLength = u16(offset + 30)
            val commentLength = u16(offset + 32)
            val localHeader = u32(offset + 42)
            if (compressedSize == 0xFFFFFFFFL || size == 0xFFFFFFFFL || localHeader == 0xFFFFFFFFL) {
                // Zip64: only files far beyond our limits need it.
                throw SpreadsheetException(SpreadsheetProblem.TOO_LARGE_UNPACKED, "zip64")
            }
            if (size > maxEntrySize) throw SpreadsheetException(SpreadsheetProblem.TOO_LARGE_UNPACKED, "entry of $size bytes")
            total += size
            if (total > maxTotalSize) throw SpreadsheetException(SpreadsheetProblem.TOO_LARGE_UNPACKED, "$total bytes in all")
            if (offset + 46 + nameLength > bytes.size) throw corrupt("bad central directory")
            val name = bytes.decodeToString(offset + 46, offset + 46 + nameLength)
            result[name] = Entry(method, flags, compressedSize.toInt(), size.toInt(), localHeader.toInt())
            at = offset.toLong() + 46 + nameLength + extraLength + commentLength
        }
        return result
    }

    private fun corrupt(detail: String) = SpreadsheetException(SpreadsheetProblem.CORRUPT, detail)

    private fun u16(at: Int): Int {
        if (at < 0 || at + 2 > bytes.size) throw corrupt("truncated zip")
        return (bytes[at].toInt() and 0xFF) or ((bytes[at + 1].toInt() and 0xFF) shl 8)
    }

    private fun u32(at: Int): Long {
        if (at < 0 || at + 4 > bytes.size) throw corrupt("truncated zip")
        return (u16(at).toLong()) or (u16(at + 2).toLong() shl 16)
    }

    companion object {
        /** More entries than any spreadsheet has (a workbook of a few sheets has a dozen or two). */
        const val MAX_ENTRIES = 1000
        /** What one part may unpack to (a sheet of thousands of rows is a few MB). */
        const val MAX_ENTRY_SIZE = 32 * 1024 * 1024
        /** What all parts together may unpack to. */
        const val MAX_TOTAL_SIZE = 64 * 1024 * 1024

        private const val LOCAL_SIGNATURE = 0x04034b50L
        private const val CENTRAL_SIGNATURE = 0x02014b50L
        private const val END_SIGNATURE = 0x06054b50L
        private const val FLAG_ENCRYPTED = 0x1
        private const val METHOD_STORED = 0
        private const val METHOD_DEFLATED = 8

        /** Whether [bytes] start like a zip file (an .xlsx), not like text (a CSV). */
        fun looksLikeZip(bytes: ByteArray): Boolean =
            bytes.size >= 4 && bytes[0] == 0x50.toByte() && bytes[1] == 0x4B.toByte() && bytes[2] == 0x03.toByte() && bytes[3] == 0x04.toByte()
    }
}

/**
 * Inflates raw DEFLATE data (RFC 1951, no zlib or gzip header) natively: java.util.zip.Inflater on
 * the JVM and Android, DecompressionStream("deflate-raw") in the browser (the plain-Kotlin
 * [Inflate] where the browser lacks it), the Compression framework on iOS. Output beyond
 * [maxOutput] bytes fails ([SpreadsheetProblem.TOO_LARGE_UNPACKED]) instead of being produced.
 */
expect suspend fun inflateRaw(data: ByteArray, maxOutput: Int): ByteArray

/** The failure every platform reports when the data inflates to more than allowed. */
internal fun inflatedTooLarge(): SpreadsheetException = SpreadsheetException(SpreadsheetProblem.TOO_LARGE_UNPACKED, "entry inflates beyond its size")
